import React, { useState } from 'react';
import { X, Search, CheckCircle2 } from 'lucide-react';

interface DislikeReasonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (reasons: string) => void;
  userName: string;
}

const REASON_CATEGORIES = [
  {
    title: "Height",
    reasons: ["Too short", "Too tall"]
  },
  {
    title: "Weight / Body Type",
    reasons: ["Too slim", "Too heavy"]
  },
  {
    title: "Location",
    reasons: ["Too far", "Different city", "Different state"]
  },
  {
    title: "Education",
    reasons: ["Qualification gap", "Field mismatch"]
  },
  {
    title: "Profession",
    reasons: ["Not working", "Wrong field", "Income gap"]
  },
  {
    title: "Lifestyle / Family",
    reasons: ["Food habits", "Family type", "Traditions"]
  },
  {
    title: "Personality / Vibe",
    reasons: ["Attitude mismatch", "Interests mismatch", "Communication style"]
  },
  {
    title: "Appearance / Photos",
    reasons: ["Not attractive", "Poor photo quality", "Doesn't match profile"]
  },
  {
    title: "Astrology / Guna Milan",
    reasons: ["Kundli mismatch", "Manglik issue", "Dosha concern"]
  },
  {
    title: "No Connection",
    reasons: ["Not My Type"]
  }
];

const DislikeReasonModal: React.FC<DislikeReasonModalProps> = ({ isOpen, onClose, onSubmit, userName }) => {
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  if (!isOpen) return null;

  const toggleReason = (reason: string) => {
    setSelectedReasons(prev => 
      prev.includes(reason) 
        ? prev.filter(r => r !== reason) 
        : [...prev, reason]
    );
  };

  const handleClear = () => setSelectedReasons([]);

  const handleSubmit = () => {
    if (selectedReasons.length === 0) {
      alert("Please select at least one reason");
      return;
    }
    onSubmit(selectedReasons.join(", "));
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-slate-900 border border-slate-700 w-full max-w-4xl max-h-[90vh] rounded-24 shadow-2xl overflow-hidden flex flex-col scale-in">
        {/* Header */}
        <div className="p-6 border-b border-slate-800 bg-slate-900/50 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black text-white flex items-center gap-3">
              <span className="p-2 bg-rose-500/20 text-rose-500 rounded-xl">
                <X size={24} />
              </span>
              Dislike Profile: <span className="text-rose-400">{userName}</span>
            </h2>
            <p className="text-slate-500 text-xs mt-1 font-bold uppercase tracking-widest">Select reasons for disliking this match</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Search & Selection Info */}
        <div className="px-6 py-4 bg-slate-950/50 border-b border-slate-800 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input 
              type="text"
              placeholder="Search reasons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-10 bg-slate-900 border border-slate-800 rounded-xl px-10 text-sm text-white focus:ring-2 focus:ring-rose-500/50 outline-none transition-all"
            />
          </div>
          <div className="flex items-center gap-4 whitespace-nowrap">
            <span className="text-xs font-bold text-slate-400">
              Selected: <span className="text-rose-400">{selectedReasons.length}</span>
            </span>
            {selectedReasons.length > 0 && (
              <button 
                onClick={handleClear}
                className="text-xs font-black text-rose-500 hover:text-rose-400 uppercase tracking-widest"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REASON_CATEGORIES.map((cat, idx) => {
              const filteredReasons = cat.reasons.filter(r => 
                r.toLowerCase().includes(searchTerm.toLowerCase()) || 
                cat.title.toLowerCase().includes(searchTerm.toLowerCase())
              );

              if (filteredReasons.length === 0) return null;

              return (
                <div key={idx} className="space-y-3 p-4 bg-slate-800/30 rounded-2xl border border-slate-700/30 hover:border-slate-600/50 transition-all group">
                  <h3 className="text-[10px] font-black text-rose-400/70 uppercase tracking-[0.2em] px-1 group-hover:text-rose-400 transition-colors">
                    {cat.title}
                  </h3>
                  <div className="space-y-2">
                    {filteredReasons.map((reason) => (
                      <label 
                        key={reason}
                        className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all border ${
                          selectedReasons.includes(reason)
                            ? 'bg-rose-500/10 border-rose-500/30 text-rose-200'
                            : 'bg-slate-900/50 border-transparent text-slate-400 hover:bg-slate-800'
                        }`}
                      >
                        <div className="relative flex items-center justify-center">
                          <input 
                            type="checkbox"
                            checked={selectedReasons.includes(reason)}
                            onChange={() => toggleReason(reason)}
                            className="sr-only"
                          />
                          <div className={`w-5 h-5 rounded-lg border-2 transition-all flex items-center justify-center ${
                            selectedReasons.includes(reason)
                              ? 'bg-rose-500 border-rose-500 scale-110 shadow-[0_0_10px_rgba(244,63,94,0.3)]'
                              : 'bg-slate-950 border-slate-700'
                          }`}>
                            {selectedReasons.includes(reason) && <CheckCircle2 size={12} className="text-white" />}
                          </div>
                        </div>
                        <span className="text-xs font-medium">{reason}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 bg-slate-950/80 border-t border-slate-800 flex justify-end gap-4">
          <button 
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white text-xs font-black uppercase tracking-widest transition-all active:scale-95"
          >
            Cancel
          </button>
          <button 
            onClick={handleSubmit}
            className="px-8 py-2.5 rounded-xl bg-rose-500 hover:bg-rose-400 text-white text-xs font-black uppercase tracking-widest shadow-lg shadow-rose-500/20 transition-all active:scale-95 flex items-center gap-2"
          >
            Submit Dislike
          </button>
        </div>
      </div>
    </div>
  );
};

export default DislikeReasonModal;
